import { useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import {
  FormattedInput,
  HiddenLabel,
  Input,
  NumberFormatter,
  PopoverOutlet,
  Select,
  Text,
} from '@figma/fpl-components';
import { Icon16Image, Icon24Image } from '@figma/fpl-icons';
import { DrawStyleSlider } from '@/UI/Slider/DrawStyleSlider';
import { useResolvedAssets } from '@/state/workflow.state';
import { assetThumbnailUrl } from '@/utils/assetUrls';
import { I18N_KEYS } from '@/language/keys';
import { minWeight, maxWeight } from '@/components/Recipe/Views/LoraView';
import { styles as inputStyles } from './ToolInputControls.styles';
import { styles } from './ToolLoraInput.styles';
import type { ToolLoraOption } from '@/api/toolRunData.api';

export type ToolLoraValue = { selectedLora: ToolLoraOption | null; weight: number };

const WEIGHT_FORMATTER = new NumberFormatter({
  min: minWeight,
  max: maxWeight,
  nudge: 0.01,
  bigNudge: 0.1,
  maximumFractionDigits: 2,
});

function LoraThumbnail({
  option,
  inMenu = false,
  disabled = false,
}: {
  option: ToolLoraOption;
  inMenu?: boolean;
  disabled?: boolean;
}) {
  const resolvedAssets = useResolvedAssets();
  const url = assetThumbnailUrl(option.coverImage, resolvedAssets, { type: 'image' });
  const [failedUrl, setFailedUrl] = useState<string>();
  const Icon = inMenu ? Icon24Image : Icon16Image;
  return (
    <span {...stylex.props(styles.thumbnail, inMenu && styles.menuThumbnail, disabled && styles.disabledThumbnail)}>
      {url && url !== failedUrl ? (
        <img src={url} alt="" onError={() => setFailedUrl(url)} {...stylex.props(styles.thumbnailImage)} />
      ) : (
        <Icon />
      )}
    </span>
  );
}

export function ToolLoraInput({
  id,
  label,
  options,
  value,
  onChange,
  disabled,
  error,
}: {
  id: string;
  label: string;
  options: ToolLoraOption[];
  value: ToolLoraValue;
  onChange: (value: ToolLoraValue) => void;
  disabled: boolean;
  error?: string;
}) {
  const { t } = useTranslation();
  const selected = options.find((option) => option.id === value.selectedLora?.id);
  const none = t(I18N_KEYS.SHARED_DESIGN_APP.INPUTS.MULTI_LORA.NONE);
  const weightLabel = t(I18N_KEYS.GENERAL.LORA_WEIGHT);
  const setWeight = (weight: number) => onChange({ ...value, weight });
  return (
    <div {...stylex.props(styles.control)}>
      <PopoverOutlet>
        <Select.Root
          value={selected?.id ?? ''}
          onChange={(selectedId) =>
            onChange({ ...value, selectedLora: options.find((option) => option.id === selectedId) ?? null })
          }
        >
          <Select.Trigger width="fill" label={<HiddenLabel>{label}</HiddenLabel>} disabled={disabled}>
            <span {...stylex.props(styles.triggerContent)}>
              {selected && <LoraThumbnail option={selected} disabled={disabled} />}
              <span {...stylex.props(styles.optionLabel)}>{selected?.name ?? none}</span>
            </span>
          </Select.Trigger>
          <Select.Container>
            <Select.Option value="" title={none}>
              {none}
            </Select.Option>
            {options.length > 0 && <Select.Separator />}
            {options.map((option) => (
              <Select.Option key={option.id} value={option.id} title={option.name}>
                <span {...stylex.props(styles.optionContent)}>
                  <LoraThumbnail option={option} inMenu />
                  <span {...stylex.props(styles.optionLabel)}>{option.name}</span>
                </span>
              </Select.Option>
            ))}
          </Select.Container>
        </Select.Root>
      </PopoverOutlet>
      {selected && (
        <div {...stylex.props(styles.weightRow)}>
          <label htmlFor={`${id}-weight`}>
            <Text.span variant="body-medium" color={disabled ? 'disabled' : 'secondary'}>
              {weightLabel}
            </Text.span>
          </label>
          <div {...stylex.props(styles.weightControl)}>
            <Input.Group columns="1fr 56px">
              <div {...stylex.props(inputStyles.sliderWrapper)}>
                <DrawStyleSlider
                  aria-label={`${label} ${weightLabel}`}
                  value={value.weight}
                  min={minWeight}
                  max={maxWeight}
                  step={0.01}
                  bigStep={0.1}
                  rangeAnchor={minWeight}
                  onChange={setWeight}
                  disabled={disabled}
                />
              </div>
              <FormattedInput.Root>
                <FormattedInput.Field<number>
                  id={`${id}-weight`}
                  aria-label={`${label} ${weightLabel}`}
                  formatter={WEIGHT_FORMATTER}
                  inputMode="decimal"
                  value={value.weight}
                  onChange={setWeight}
                  disabled={disabled}
                />
              </FormattedInput.Root>
            </Input.Group>
          </div>
        </div>
      )}
      {error && (
        <Text.div variant="body-medium" color="danger" role="alert">
          {error}
        </Text.div>
      )}
    </div>
  );
}
